import React, { useEffect } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import WizardFormFirstPage from './CreateEntityAccount';
import WizardFormSecondPage from './CreateEntityFirstQues';
import WizardFormThirdPage from './CreateEntitySecondQues';
import WizardFormFourthPage from './CreateEntityThirdQues';
import WizardFormFifthPage from './CreateEntityLastQues';
import WizardFormSixthPage from './BusinessInformation';
import WizardFormSevenPage from './SummaryForm';
import { setUserID, setEmail, setKycToken, setEmailVerify, setEntityAccountUserDetails, setEntityAccountBusinessDetails, setBusinessAddressDetails, setPhysicalBusinessAddressDetails, setAccountUseDetails, setAuthorizedRepresentativeDetails, setUploadDocumentDetails, setInvites, setwizardPageName, setwizardPageNumber, setCertificationDetails } from '../../EntityAccountReducer';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import Header from "MyComponents/Includes/Header";
import Footer from "MyComponents/Includes/Footer";
import { validateKycToken } from '../../../Utils/user';
import { getEntityKycDraft, updateEntityKycDraftStep } from "../../../Utils/entityKycDraft";
import { ToastBox, notifyError } from "MyComponents/Toaster";
import BackButton from "../../Includes/BackButtonEntityAccount";
import Loader from "MyComponents/UI/Loader";
import OfflineBanner from "MyComponents/UI/OfflineBanner";
import { Box, Step, StepLabel, Stepper, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import StepConnector, { stepConnectorClasses } from '@mui/material/StepConnector';
import { StepIconProps } from '@mui/material/StepIcon';
import useNetworkStatus from '../../../hooks/useNetworkStatus';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button as MuiButton } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface Props {
  onSubmit?: any;
}

const CustomConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.vertical}`]: {
    marginLeft: 15,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: '#e0e0e0',
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: '#e0e0e0',
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: '#e0e0e0',
    borderLeftWidth: 2,
    borderRadius: 1,
    minHeight: '24px',
  },
}));

const CustomStepIconRoot = styled('div')<{ ownerState: { completed?: boolean; active?: boolean; error?: boolean } }>(
  ({ theme, ownerState }) => ({
    backgroundColor: ownerState.active || ownerState.completed ? '#00D084' : '#A9B5C1',
    zIndex: 1,
    color: '#fff',
    width: 30,
    height: 30,
    display: 'flex',
    borderRadius: '50%',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '0.85rem',
    fontWeight: 'normal',
    ...(ownerState.error && {
      backgroundColor: '#f44336',
    }),
  }),
);

function CustomStepIcon(props: StepIconProps) {
  const { active, completed, error, className, icon } = props;

  return (
    <CustomStepIconRoot ownerState={{ completed, active, error }} className={className}>
      {completed ? '✓' : String(icon)}
    </CustomStepIconRoot>
  );
}

let _isValidationTriggered = false;

const WizardEntityAccountKyc: React.FC<Props> = (props: any) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const page = useSelector((state: RootState) => state.entityAccount.wizardPageNumber);
  const stepValidation = useSelector(
    (state: RootState) => state.entityAccount.wizardStepValidation
  );
  const isStepValid = (step: number) => stepValidation[step];

  const isStepFailed = (step: number) => {
    return !isStepValid(step);
  };

  const navigate = useNavigate();
  const { userId, kycToken } = useParams<{ userId: string, kycToken: string }>();

  const { isOnline } = useNetworkStatus();
  const [showResumeDialog, setShowResumeDialog] = React.useState(false);
  const [showSubmittedDialog, setShowSubmittedDialog] = React.useState(false);
  const [fetchedDraft, setFetchedDraft] = React.useState<any>(null);

  const pageName = useSelector((state: RootState) => state.entityAccount.pageName);
  const isWizardReady = React.useRef(false);

  const getSubStepNumber = (name: string): number => {
    switch (name) {
      case 'businessInfo': return 1;
      case 'businessPhysicalAddress': return 2;
      case 'accountUse': return 3;
      case 'successBusinessInfo': return 3;
      case 'authorizedRep': return 4;
      case 'beneficialOwners': return 5;
      case 'directors': return 6;
      case 'successRelatedParties': return 6;
      case 'uploaddoc': return 7;
      case 'certification': return 8;
      default: return 1;
    }
  };

  const steps = [
    t('kyc.step_getting_started', 'Getting Started'),
    t('kyc.step_registered_business', 'Registered Business'),
    t('kyc.step_business_identity', 'Business Identity'),
    t('kyc.step_authorized_rep_check', 'Authorized Rep Check'),
    t('kyc.step_verification_req', 'Verification Requirements'),
    page === 6 ? `${t('kyc.step_business_application', 'Business Application')} (${getSubStepNumber(pageName)}/8)` : t('kyc.step_business_application', 'Business Application'),
    t('kyc.step_summary', 'Summary & Verification'),
  ];

  useEffect(() => {
    if (_isValidationTriggered) return;
    _isValidationTriggered = true;

    const validate = async () => {
      if (!userId || !kycToken) {
        navigate("/404");
        return;
      }

      console.log("WizardEntityAccountKyc: Performing token validation...");
      try {
        const validationResult = await validateKycToken(userId, kycToken);
        if (!validationResult) {
          navigate("/404");
          return;
        }

        dispatch(setUserID(userId));
        dispatch(setKycToken(kycToken));

        const draft = await getEntityKycDraft(userId);
        if (draft) {
          const { status, draftData } = draft;

          if (status === 'submitted') {
            setShowSubmittedDialog(true);
          } else if (draftData && (draftData.EntityAccountUser || draftData.businessInformation || draftData.directors?.length > 0 || draftData.wizardState)) {
            setFetchedDraft(draftData);
            setShowResumeDialog(true);
          } else {
            dispatch(setwizardPageNumber(1));
            localStorage.setItem('pageNumber', '1');
            isWizardReady.current = true;
          }
        } else {
          dispatch(setwizardPageNumber(1));
          localStorage.setItem('pageNumber', '1');
          isWizardReady.current = true;
        }

      } catch (error) {
        console.error("WizardEntityAccountKyc: Validation failed:", error);
      }
    };

    validate();
  }, [userId, kycToken, dispatch, navigate]);

  useEffect(() => {
    if (!isWizardReady.current || !userId) return;

    const syncState = async () => {
      console.log(`WizardEntityAccountKyc: Syncing wizardState (Page ${page}, Name ${pageName}) to draft...`);
      try {
        await updateEntityKycDraftStep(userId, 'wizardState', {
          pageNumber: page,
          pageName: pageName
        });
      } catch (error) {
        console.error("WizardEntityAccountKyc: Failed to sync wizard state:", error);
      }
    };

    syncState();
  }, [page, pageName, userId]);

  const handleResume = () => {
    if (!fetchedDraft) return;

    console.log("WizardEntityAccountKyc: Resuming progress...", fetchedDraft);

    if (fetchedDraft) {
      const userGroup = fetchedDraft.EntityAccountUser || {};
      const normalizedUserDetails = {
        firstName: userGroup.firstName || fetchedDraft.firstName || "",
        lastName: userGroup.lastName || fetchedDraft.lastName || "",
        entityUserName: userGroup.entityUserName || fetchedDraft.entityUserName || userGroup.userName || fetchedDraft.userName || "",
        entityBusinessName: userGroup.entityBusinessName || fetchedDraft.entityBusinessName || userGroup.businessName || fetchedDraft.businessName || "",
        country: userGroup.country || fetchedDraft.country || null,
        RegisteredBusiness: userGroup.registeredBusiness || userGroup.RegisteredBusiness || fetchedDraft.registeredBusiness || fetchedDraft.RegisteredBusiness || "",
        AuthorizedRepresentative: userGroup.isAuthorizedRepresentative || userGroup.AuthorizedRepresentative || fetchedDraft.isAuthorizedRepresentative || fetchedDraft.AuthorizedRepresentative || "",
        businessEntityName: userGroup.businessEntityName || userGroup.businessLegalEntityName || fetchedDraft.businessEntityName || "",
        businessCountry: userGroup.businessCountry || userGroup.businessCountryOfRegistration || fetchedDraft.businessCountry || null,
        businessState: userGroup.businessState || userGroup.businessStateOfRegistration || fetchedDraft.businessState || "",
        businessNature: userGroup.businessNature || userGroup.natureOfBusiness || fetchedDraft.businessNature || "",
        businessWebsiteName: userGroup.businessWebsiteName || userGroup.businessWebsite || fetchedDraft.businessWebsiteName || "",
      };

      dispatch(setEntityAccountUserDetails(normalizedUserDetails));
      dispatch(setEntityAccountBusinessDetails(normalizedUserDetails));
    }
    if (fetchedDraft.businessInformation) {
      const normalizedBusinessInfo = {
        ...fetchedDraft.businessInformation,
        businessDocuments: fetchedDraft.businessInformation.businessDocuments || fetchedDraft.businessInformation.businessDocumentImages
      };
      dispatch(setBusinessAddressDetails(normalizedBusinessInfo));
    }
    if (fetchedDraft.physicalBusinessAddress) {
      const normalizedAddress = {
        ...fetchedDraft.physicalBusinessAddress,
        isBusinessOfficeOrRemote: fetchedDraft.physicalBusinessAddress.isBusinessOfficeOrRemote || fetchedDraft.physicalBusinessAddress.IsBusinessOfficeOrRemote,
        physicalBusinessProofOfAddress: fetchedDraft.physicalBusinessAddress.physicalBusinessProofOfAddress || fetchedDraft.physicalBusinessAddress.proofOfAddressDocument
      };
      dispatch(setPhysicalBusinessAddressDetails(normalizedAddress));
    }
    if (fetchedDraft.accountUse) {
      const normalizedAccountUse = {
        ...fetchedDraft.accountUse,
        reasonForUsingAccount: fetchedDraft.accountUse.reasonForUsingAccount || fetchedDraft.accountUse.reasonForAccountUse,
        accountUseCustomerLocated: fetchedDraft.accountUse.accountUseCustomerLocated || fetchedDraft.accountUse.customersLocation
      };
      dispatch(setAccountUseDetails(normalizedAccountUse));
    }
    if (fetchedDraft.uploadedDocuments) {
      const normalizedDocs = {
        ...fetchedDraft.uploadedDocuments,
        organizationalDocument: fetchedDraft.uploadedDocuments.organizationalDocument || fetchedDraft.uploadedDocuments.OrganizationalDocument,
        proofPhysicalBusinessAddress: fetchedDraft.uploadedDocuments.proofPhysicalBusinessAddress || fetchedDraft.uploadedDocuments.ProofOfPhysicalBusinessAddress
      };
      dispatch(setUploadDocumentDetails(normalizedDocs));
    }
    if (fetchedDraft.certification) {
      const normalizedCert = typeof fetchedDraft.certification === 'object'
        ? fetchedDraft.certification
        : { certification1: true, certification2: true, certificationNotes: fetchedDraft.certification };
      dispatch(setCertificationDetails(normalizedCert));
    }

    if (fetchedDraft.authorizedRepresentatives && fetchedDraft.authorizedRepresentatives.length > 0) {
      dispatch(setAuthorizedRepresentativeDetails(fetchedDraft.authorizedRepresentatives[0]));
    }

    const combinedInvites: any[] = [];
    if (fetchedDraft.authorizedRepresentatives) {
      fetchedDraft.authorizedRepresentatives.forEach((r: any) => combinedInvites.push({ ...r, userRole: 'authRep', Role: 'authRep' }));
    }
    if (fetchedDraft.beneficialOwners) {
      fetchedDraft.beneficialOwners.forEach((b: any) => combinedInvites.push({ ...b, userRole: 'beneficialOwner', Role: 'beneficialOwner' }));
    }
    if (fetchedDraft.directors) {
      fetchedDraft.directors.forEach((d: any) => combinedInvites.push({ ...d, userRole: 'director', Role: 'director' }));
    }
    dispatch(setInvites(combinedInvites));

    let targetPage = 1;
    let targetPageName = "";

    if (fetchedDraft.wizardState) {
      targetPage = fetchedDraft.wizardState.pageNumber || 1;
      targetPageName = fetchedDraft.wizardState.pageName || "";
    } else {
      if (fetchedDraft.certification || fetchedDraft.uploadedDocuments) {
        targetPage = 7;
      } else if (fetchedDraft.directors?.length > 0) {
        targetPage = 6;
        targetPageName = "directors";
      } else if (fetchedDraft.beneficialOwners?.length > 0) {
        targetPage = 6;
        targetPageName = "beneficialOwners";
      } else if (fetchedDraft.authorizedRepresentatives?.length > 0) {
        targetPage = 6;
        targetPageName = "authorizedRep";
      } else if (fetchedDraft.accountUse) {
        targetPage = 6;
        targetPageName = "accountUse";
      } else if (fetchedDraft.physicalBusinessAddress) {
        targetPage = 6;
        targetPageName = "businessPhysicalAddress";
      } else if (fetchedDraft.businessInformation) {
        targetPage = 6;
        targetPageName = "businessInfo";
      } else if (fetchedDraft.EntityAccountUser) {
        targetPage = 2;
      }
    }

    dispatch(setwizardPageNumber(targetPage));
    localStorage.setItem('pageNumber', targetPage.toString());
    if (targetPageName) {
      dispatch(setwizardPageName(targetPageName));
      localStorage.setItem('pageName', targetPageName);
    }

    isWizardReady.current = true;
    setShowResumeDialog(false);
  };

  const handleStartFresh = () => {
    dispatch(setwizardPageNumber(1));
    localStorage.setItem('pageNumber', '1');
    isWizardReady.current = true;
    setShowResumeDialog(false);
  };

  const getStepIndex = (pageNum: number, name: string): number => {
    if (pageNum === 1) return 0;
    if (pageNum === 2) return 1;
    if (pageNum === 3) return 2;
    if (pageNum === 4) return 3;
    if (pageNum === 5) return 4;
    if (pageNum === 6) return 5;
    if (pageNum === 7) return 6;
    return 0;
  };

  const handleStepClick = (index: number) => {
    if (index <= maxReachedStep) {
      if (index === 0) dispatch(setwizardPageNumber(1));
      else if (index === 1) dispatch(setwizardPageNumber(2));
      else if (index === 2) dispatch(setwizardPageNumber(3));
      else if (index === 3) dispatch(setwizardPageNumber(4));
      else if (index === 4) dispatch(setwizardPageNumber(5));
      else if (index === 5) {
        dispatch(setwizardPageNumber(6));
        dispatch(setwizardPageName('businessInfo'));
        localStorage.setItem('pageName', 'businessInfo');
      }
      else if (index === 6) dispatch(setwizardPageNumber(7));
    }
  };

  const activeStepIndex = getStepIndex(page, pageName);

  const [maxReachedStep, setMaxReachedStep] = React.useState(0);
  useEffect(() => {
    if (activeStepIndex > maxReachedStep) {
      setMaxReachedStep(activeStepIndex);
    }
  }, [activeStepIndex, maxReachedStep]);

  return (
    <>
      {/* Header removed from here */}
      <OfflineBanner isOnline={isOnline} />
      <ToastBox />
      <Box sx={{ display: "flex" }}>
        <Box
          sx={{
            display: { xs: 'none', lg: 'block' },
            width: "280px",
            minWidth: "280px",
            paddingLeft: "3rem",
            marginTop: "6em",
            marginBottom: "3em",
            position: 'sticky',
            top: '6em',
            maxHeight: 'calc(100vh - 7em)',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#cfd8dc',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
            },
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 4, color: '#0B1628' }}>
            {t('kyc.entity_verification_title')}
          </Typography>
          <Stepper
            activeStep={activeStepIndex}
            orientation="vertical"
            connector={<CustomConnector />}
            sx={{ width: "100%" }}
          >
            {steps.map((label, index) => {
              const labelProps = {
                error: isStepFailed(index + 1),
              };

              return (
                <Step key={label} completed={index < maxReachedStep && index !== activeStepIndex}>
                  <StepLabel
                    {...labelProps}
                    StepIconComponent={CustomStepIcon}
                    onClick={() => handleStepClick(index)}
                    sx={{
                      cursor: index <= maxReachedStep ? 'pointer' : 'default',
                      '& .MuiStepLabel-label': {
                        fontSize: '0.85rem',
                        color: '#A9B5C1',
                        transition: 'color 0.3s, font-weight 0.3s',
                      },
                      '& .MuiStepLabel-label.Mui-active': {
                        color: '#00D084',
                        fontWeight: 'bold',
                      },
                      '& .MuiStepLabel-label.Mui-completed': {
                        color: '#A9B5C1',
                      },
                      '& .MuiStepLabel-label.Mui-error': {
                        color: '#f44336',
                      },
                    }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>
        </Box>

        <Box
          sx={{
            flex: 1,
            paddingTop: "1em",
            paddingLeft: { xs: "1.2rem", md: "3rem", lg: "2rem" },
            paddingRight: { xs: "1.2rem", md: "3rem", lg: "2rem" },
            maxWidth: { lg: 'calc(100% - 280px)' },
            marginBottom: "3em"
          }}
        >
          {/* Mobile / Tablet Step Indicator */}
          <Box sx={{ display: { xs: 'block', lg: 'none' }, mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1E344E' }}>
              KYB — Step {activeStepIndex + 1} of {steps.length}: {steps[activeStepIndex]}
            </Typography>
            <Box sx={{ width: '100%', height: '4px', backgroundColor: '#e0e0e0', mt: 1, borderRadius: '2px' }}>
              <Box sx={{ width: `${((activeStepIndex + 1) / steps.length) * 100}%`, height: '100%', backgroundColor: '#00D084', borderRadius: '2px' }} />
            </Box>
          </Box>

          {page > 1 && (
            <Box sx={{ mb: 2, display: 'inline-block' }}>
              <BackButton />
            </Box>
          )}

          <div className="wizard-content-wrapper">
            {page === 1 && <WizardFormFirstPage />}
            {page === 2 && <WizardFormSecondPage />}
            {page === 3 && <WizardFormThirdPage />}
            {page === 4 && <WizardFormFourthPage />}
            {page === 5 && <WizardFormFifthPage />}
            {page === 6 && <WizardFormSixthPage />}
            {page === 7 && <WizardFormSevenPage />}
          </div>
        </Box>
      </Box>
      <Footer full={true} />

      <Dialog
        open={showResumeDialog}
        PaperProps={{
          sx: { borderRadius: '12px', padding: '8px' }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: '#0B1628' }}>
          {t('kyc.resume_progress')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary">
            {t('kyc.resume_progress_desc')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '16px', gap: 1 }}>
          <MuiButton
            onClick={handleStartFresh}
            variant="outlined"
            sx={{ borderRadius: '8px', textTransform: 'none', color: '#707070', borderColor: '#e0e0e0' }}
          >
            {t('kyc.start_fresh')}
          </MuiButton>
          <MuiButton
            onClick={handleResume}
            variant="contained"
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              px: 4,
              backgroundColor: '#00D084',
              color: '#fff',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#00b873', boxShadow: 'none' }
            }}
          >
            {t('kyc.resume')}
          </MuiButton>
        </DialogActions>
      </Dialog>
 
      <Dialog
        open={showSubmittedDialog}
        onClose={() => navigate("/personal_account_created")}
        PaperProps={{
          sx: { borderRadius: '12px', padding: '8px' }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: '#0B1628' }}>
          {t('kyc.already_submitted')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary">
            {t('kyc.already_submitted_desc')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '16px' }}>
          <MuiButton
            onClick={() => navigate("/personal_account_created")}
            variant="contained"
            fullWidth
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              backgroundColor: '#00D084',
              color: '#fff',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#00b873', boxShadow: 'none' }
            }}
          >
            {t('kyc.go_dashboard')}
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};


export default WizardEntityAccountKyc;
